import React from "react";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import { makeStyles } from "@material-ui/core/styles";
import { KeyboardDateTimePicker } from "@material-ui/pickers";

import {
  SupportDateRangePresets,
  type SupportDateRangePresetId,
  rangeForSupportDatePreset,
} from "./dateRangePresets";

const useStyles = makeStyles((theme) => ({
  controls: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  presetButton: {
    textTransform: "none",
  },
  dateRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    alignItems: "flex-end",
    marginBottom: theme.spacing(2),
  },
  dateField: {
    minWidth: 240,
  },
}));

export interface SupportDateRangeFilterProps {
  from: Date;
  to: Date;
  activePreset: SupportDateRangePresetId | null;
  onPresetChange: (
    presetId: SupportDateRangePresetId,
    from: Date,
    to: Date
  ) => void;
  onFromChange: (date: Date) => void;
  onToChange: (date: Date) => void;
  /** When provided, an Apply button is shown for manual range confirmation. */
  onApply?: () => void;
  fromLabel?: string;
  toLabel?: string;
  dateFormat?: string;
}

function SupportDateRangeFilter({
  from,
  to,
  activePreset,
  onPresetChange,
  onFromChange,
  onToChange,
  onApply,
  fromLabel = "From",
  toLabel = "To",
  dateFormat = "MM/dd/yyyy hh:mm a",
}: SupportDateRangeFilterProps) {
  const classes = useStyles();

  return (
    <>
      <Box className={classes.controls}>
        {SupportDateRangePresets.map((preset) => (
          <Button
            key={preset.id}
            variant={activePreset === preset.id ? "contained" : "outlined"}
            color="primary"
            size="small"
            className={classes.presetButton}
            onClick={() => {
              const range = rangeForSupportDatePreset(preset.id);
              onPresetChange(preset.id, range.from, range.to);
            }}
          >
            {preset.label}
          </Button>
        ))}
      </Box>

      <Box className={classes.dateRow}>
        <KeyboardDateTimePicker
          className={classes.dateField}
          disableToolbar
          variant="inline"
          inputVariant="outlined"
          ampm
          label={fromLabel}
          format={dateFormat}
          value={from}
          onChange={(date) => {
            if (!date || Number.isNaN(date.getTime())) return;
            onFromChange(date);
          }}
          maxDate={to}
        />
        <KeyboardDateTimePicker
          className={classes.dateField}
          disableToolbar
          variant="inline"
          inputVariant="outlined"
          ampm
          label={toLabel}
          format={dateFormat}
          value={to}
          onChange={(date) => {
            if (!date || Number.isNaN(date.getTime())) return;
            onToChange(date);
          }}
          minDate={from}
          maxDate={new Date()}
        />
        {onApply ? (
          <Button variant="contained" color="primary" onClick={onApply}>
            Apply
          </Button>
        ) : null}
      </Box>
    </>
  );
}

export default SupportDateRangeFilter;
